import React, { SFC, useEffect } from "react";

/**
 * Props interface for the Text component
 * @interface TextProps
 * @property {string} text - The text content to be displayed
 * @property {string} [className] - Optional CSS class name for styling
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this text
 * @property {() => void} [onRendered] - Optional callback function triggered after text is rendered
 */
export interface TextProps {
    text: string;
    className?: string;
    visibleTo?: string[];
    onRendered?: () => void;
}

/**
 * A simple text display component that renders text content with optional styling
 * and visibility control.
 * 
 * @component
 * @param {TextProps} props - The component props
 * @returns {JSX.Element} A div containing the text content
 * 
 * @example
 * ```tsx
 * <Text 
 *   text="Hello World" 
 *   className="custom-style"
 *   onRendered={() => console.log('Text rendered')}
 * />
 * ```
 */
const Text: SFC<TextProps> = (props) => {
    const { text, className, onRendered } = props;
    const css = [
        "__text__",
        className ? className : null,
    ].join(" ").trim();

    // events
    const handleRendered = () => (onRendered && onRendered());

    // this should fire on mount/update
    useEffect(() => handleRendered());

    return <div className={css}>{text}</div>;
};

export default Text;
